import { useEffect, useState } from 'react';

interface Props {
  value?: number | string;
  onChange: (value: number | undefined) => void;
}

// Rango razonable para tamaños de texto, en px (objetivo en desktop).
const MIN_PX = 12;
const MAX_PX = 160;

/**
 * Tamaño de texto por elemento, editado en **píxeles** (objetivo en desktop).
 * Vacío = automático (el tamaño fluido natural del elemento). El renderer envuelve
 * el px en `clamp()` para que baje en móvil sin desbordar.
 *
 * Compat: el contenido viejo guardaba un enum (`xs|sm|md|lg|xl`). Si llega un string
 * se muestra como "automático" hasta que el usuario teclee un px (que lo reemplaza).
 *
 * El rango se aplica **al confirmar** (blur), no en cada tecla: clampear mientras se
 * escribe corrompe el valor (escribir "35" empezaba en "3"→clamp a 12 y terminaba en "125").
 */
export function PxSizeField({ value, onChange }: Props) {
  const isLegacy = typeof value === 'string' && value !== '';
  const externalText = typeof value === 'number' ? String(value) : '';
  // Texto en edición. Se re-sincroniza si el prop externo cambia (p. ej. otro bloque).
  const [text, setText] = useState(externalText);
  useEffect(() => { setText(externalText); }, [externalText]);

  const commit = () => {
    const trimmed = text.trim();
    if (trimmed === '') { onChange(undefined); setText(''); return; }
    const n = Math.min(MAX_PX, Math.max(MIN_PX, Math.round(Number(trimmed))));
    if (Number.isFinite(n)) { onChange(n); setText(String(n)); }
    else { onChange(undefined); setText(''); }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={MIN_PX}
          max={MAX_PX}
          step={1}
          value={text}
          placeholder="Automático"
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
        />
        <span className="text-sm text-ui-text-subtle">px</span>
      </div>
      <p className="text-sm text-ui-text-subtle leading-snug">
        {isLegacy
          ? 'Tamaño anterior aplicado. Escribe un valor en px para fijarlo (se adapta en móvil).'
          : 'Vacío = automático. El tamaño en px es el objetivo en escritorio y baja en móvil.'}
      </p>
    </div>
  );
}
